import { API_METHODS } from '@compass/client/constants/api-methods';
import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import type { Workflow } from '@compass/client/types/workflow';
import { ActionEmailDialog } from '@components/ActionEmailDialog';
import { ActionInputDialog } from '@components/ActionInputDialog';
import { CaseActionsDropdownItems } from '@components/CaseActionsDropdown';
import { Button } from '@components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@components/ui/dropdown-menu';
import { ChevronsUpDown } from 'lucide-react';
import React, { memo, useMemo, useState } from 'react';

import { ACTION_LABELS } from '@/constants/actions';
import { CaseState } from '@/constants/caseStates';
import { PQCWorkflowVariant } from '@/constants/dispositions';
import { useExecuteAction } from '@/hooks/useCases';
import {
  useCanCallAPI,
  useIsCaseProgramAdmin,
  useIsCurrentUserAssigned,
} from '@/hooks/usePermissions';
import { useSchema } from '@/hooks/useSchema';
import type { ActionDataParams, ActionItem } from '@/types/actions';
import type { SelectOption } from '@/types/formSchema';
import {
  actionRequiresCallReviewNote,
  actionRequiresEmailInput,
  actionRequiresIneligibilityNoteInput,
  actionRequiresIneligibilityTag,
  actionRequiresTaskInput,
  actionRequiresTextMessageInput,
  canMoveHumanCallToHumanAudit,
  canPerformAction,
  canWorkHumanAudit,
  categorizeActions,
  getAvailableActionsForCaseState,
  INELIGIBILITY_TAG_FIELD_ID,
  RESUBMISSION_RESTRICTED_ACTIONS,
} from '@/utils/caseActions';
import { formatStatus, getStatusColors } from '@/utils/cases';
import { isAIAuditState } from '@/utils/caseStateHelpers';
import { getStatusColorWithConfig } from '@/utils/status';

import { ActionCallReviewDialog } from './ActionCallReviewDialog';
import { ActionTaskDialog } from './ActionTaskDialog';
import { ActionTextDialog } from './ActionTextDialog';

/** Actions that only apply to the email variant and should be hidden for portal workflows. */
const PORTAL_EXCLUDED_ACTIONS = new Set(['submit_to_manufacturer', 'pqc_resend_email']);

interface StatusDropdownProps {
  /** Case record item */
  item: GetCaseRecordResponse;
  /** Selected workflow for state configuration */
  selectedWorkflow?: Workflow | null;
  /** Callback when action is selected */
  onActionSelect?: (action: string, caseId: string, actionDataParams?: ActionDataParams) => void;
  /** Status config (workflow `ui.status`) used to resolve the badge color + label in
   * cross-program views where `selectedWorkflow` is null (Tier-2 org page). */
  statusConfig?: Record<string, any>;
  /**
   * Action values to omit from this dropdown (e.g. when a host surfaces them
   * elsewhere, like the case-details "More options" menu). Does not affect
   * other places the dropdown is rendered.
   */
  excludeActions?: string[];
}

const StatusDropdownComponent: React.FC<StatusDropdownProps> = ({
  item,
  selectedWorkflow,
  onActionSelect,
  statusConfig,
  excludeActions,
}) => {
  const executeAction = useExecuteAction();

  // Human audit (BI-1246): "Start Human Audit" is only offered to the assignee.
  // For transportation and unifiedmedbv it also needs a Program Admin (BV-2657).
  const isCurrentUserAssigned = useIsCurrentUserAssigned();
  const isAssignedToMe = isCurrentUserAssigned(item.assigned, item.assigned_user_id);
  // Transportation and unifiedmedbv (BV-2514, BV-2657): "Move to Human Audit", and working
  // the human audit it leads to, are only for Program Admins and above.
  const isProgramAdmin = useIsCaseProgramAdmin(item.organization_id, item.program_id);

  const hasResendEmailPermission = useCanCallAPI(
    API_METHODS.ManualActionService.ExecuteResendEmailAction,
  );
  const { inputGroups } = useSchema(selectedWorkflow?.id);
  const ineligibilityOptions = useMemo<SelectOption[]>(() => {
    for (const group of inputGroups) {
      for (const field of group.fields) {
        if (field.id.toLowerCase() === INELIGIBILITY_TAG_FIELD_ID && field.options?.length) {
          return field.options;
        }
      }
    }
    return [];
  }, [inputGroups]);

  const [noteDialogState, setNoteDialogState] = useState<{
    isOpen: boolean;
    action: string;
    caseId: string;
  }>({ isOpen: false, action: '', caseId: '' });

  const [taskDialogState, setTaskDialogState] = useState<{
    isOpen: boolean;
    action: string;
    caseId: string;
  }>({ isOpen: false, action: '', caseId: '' });

  const [textDialogState, setTextDialogState] = useState<{
    isOpen: boolean;
    action: string;
    caseId: string;
  }>({ isOpen: false, action: '', caseId: '' });

  const [emailDialogState, setEmailDialogState] = useState<{
    isOpen: boolean;
    action: string;
    caseId: string;
  }>({ isOpen: false, action: '', caseId: '' });

  const [callReviewDialogState, setCallReviewDialogState] = useState<{
    isOpen: boolean;
    action: string;
    caseId: string;
  }>({ isOpen: false, action: '', caseId: '' });

  const availableActions = useMemo(() => {
    if (!selectedWorkflow || !item.state) return [];

    // A transportation or unifiedmedbv human audit is worked by Program Admins and above
    // only (BV-2657): nobody else gets its actions, Start Human Audit included.
    if (
      !canWorkHumanAudit(
        item.state,
        item.workflow_type || selectedWorkflow.workflow_type,
        isProgramAdmin,
      )
    ) {
      return [];
    }

    const isPortalVariant = selectedWorkflow?.variant === PQCWorkflowVariant.PORTAL;

    const actions: ActionItem[] = getAvailableActionsForCaseState(
      selectedWorkflow,
      item.state,
    ).filter(action => {
      if (excludeActions?.includes(action.value)) return false;

      if (!canPerformAction(action.value, item.state, item)) return false;

      // Portal variant has no email flow — hide email-related actions
      if (isPortalVariant && PORTAL_EXCLUDED_ACTIONS.has(action.value)) return false;

      // Check if this action requires a specific API permission
      const apiMethod = RESUBMISSION_RESTRICTED_ACTIONS[action.value];
      if (apiMethod && !hasResendEmailPermission) return false;

      return true;
    });

    // Every automated AI-audit state is cancellable, independent of the workflow's
    // ui.status config: the audit runs in a cancellable child workflow on hermes
    // driven by the cancel-ai-audit signal, so we guarantee the entry point here
    // rather than relying on per-program config. BV-2131 widened this from the
    // single `ai_audit` to every audit state (AI-call and human-call audits) across
    // all workflows. The `some` guard avoids a duplicate when config already lists it.
    if (isAIAuditState(item.state) && !actions.some(action => action.value === 'cancel_ai_audit')) {
      actions.push({ value: 'cancel_ai_audit', label: ACTION_LABELS.cancel_ai_audit });
    }

    // A parked refill case is always cancellable, for the same reason ai_audit is
    // guaranteed above: the capability lives in the workflow (the FSM's
    // cancel-ai-call-scheduled handler is live for exactly this state), not in a
    // program's ui.status config, so the entry point is guaranteed here rather than
    // depending on every environment's workflow document carrying it. The `some`
    // guard avoids a duplicate when a document does list it. (JNJ-109)
    if (
      item.state === CaseState.REFILL_SCHEDULED &&
      !actions.some(action => action.value === 'refill_cancel_scheduled_call')
    ) {
      actions.push({
        value: 'refill_cancel_scheduled_call',
        label: ACTION_LABELS.refill_cancel_scheduled_call,
      });
    }

    // A refill case parked in human_audit_needed always gets Retry and Skip, for
    // the same reason as refill_scheduled above: the FSM's human-audit-retry /
    // human-audit-skip handlers are live for exactly this state, whatever the
    // workflow document lists. (JNJ-172)
    if (item.state === CaseState.REFILL_HUMAN_AUDIT_NEEDED) {
      for (const value of ['refill_human_audit_retry', 'refill_human_audit_skip']) {
        if (!actions.some(action => action.value === value)) {
          actions.push({ value, label: ACTION_LABELS[value] });
        }
      }
    }

    // Likewise Mark Case as Completed for a refill case parked in
    // human_call_needed, the one state whose mark-case-as-completed handler is live.
    if (
      item.state === CaseState.REFILL_HUMAN_CALL_NEEDED &&
      !actions.some(action => action.value === 'refill_mark_case_as_completed')
    ) {
      actions.push({
        value: 'refill_mark_case_as_completed',
        label: ACTION_LABELS.refill_mark_case_as_completed,
      });
    }

    // Transportation and unifiedmedbv (BV-2514, BV-2657): a Program Admin (or a higher
    // role) can move a human call to human audit — the only human_call → human_audit edge
    // in transportation v4 and unifiedmedbv v4.
    // Guaranteed here like the entries above rather than depending on every
    // environment's workflow document; the server enforces the same role rule.
    if (
      canMoveHumanCallToHumanAudit(
        item.state,
        item.workflow_type || selectedWorkflow?.workflow_type,
        isProgramAdmin,
      ) &&
      !actions.some(action => action.value === 'human_call_move_to_human_audit')
    ) {
      actions.push({
        value: 'human_call_move_to_human_audit',
        label: ACTION_LABELS.human_call_move_to_human_audit,
      });
    }

    // Human audit (BI-1246 / BI-1323): in the "Human Audit Needed" phase
    // (human_audit_in_progress=false), the assignee gets a "Start Human Audit"
    // (for transportation and unifiedmedbv, only a Program Admin assignee; see above)
    // action at the TOP of the dropdown (unshift → leads the "Proceed" group),
    // *alongside* the normal terminal transition actions. Once in progress
    // (human_audit_in_progress=true) it is hidden (already started).
    //
    // While the summary is regenerating — which fires on the human-call →
    // human-audit transition — Start stays visible but disabled (blurred, with a
    // tooltip) so the auditor can't begin against a stale/in-flight summary.
    if (
      item.state === CaseState.HUMAN_AUDIT &&
      item.status_flags?.human_audit_in_progress !== true &&
      isAssignedToMe
    ) {
      const regenerating = item.status_flags?.is_summary_generation_in_progress === true;
      actions.unshift({
        value: 'start_human_audit',
        label: ACTION_LABELS.start_human_audit,
        disabled: regenerating,
        disabledTooltip: regenerating ? 'Summary regeneration is in progress' : undefined,
      });
    }

    return actions;
  }, [
    selectedWorkflow,
    item,
    hasResendEmailPermission,
    isAssignedToMe,
    isProgramAdmin,
    excludeActions,
  ]);

  // Get status colors with workflow loading state and active call ID
  const activeCallId = item.active_call_id;
  // Cross-program views pass a statusConfig (workflow ui.status) since there's no
  // selectedWorkflow to resolve colors from; otherwise resolve from the workflow.
  const statusColors = statusConfig
    ? getStatusColorWithConfig(item.state || '', statusConfig, activeCallId)
    : getStatusColors(item.state || '', selectedWorkflow, activeCallId);
  // If statusColors is null (workflow loading), don't show badge
  if (!statusColors) {
    return null;
  }

  // Use label from statusColors if available, otherwise use formatStatus
  let displayLabel = statusColors.label || formatStatus(item.state, selectedWorkflow);

  // Human audit (BI-1246 / BI-1323): the single human_audit state presents as
  // "Needed" before the auditor starts and "In Progress" once
  // human_audit_in_progress is set.
  if (item.state === CaseState.HUMAN_AUDIT) {
    displayLabel = item.status_flags?.human_audit_in_progress
      ? 'Human Audit In Progress'
      : 'Human Audit Needed';
  }

  // A state is actionable when the backend flags it (manual task / active AI
  // call) or when it's the ai_audit state — which is always cancellable but
  // sets no dedicated status flag, so we gate it on the state name here.
  //
  // refill_scheduled is here for the same reason as ai_audit, and it is not
  // optional: the refill FSM makes no SetStatusFlags call at all, so a parked
  // refill case sets NONE of these flags and the dropdown would never render for
  // it — no matter what its workflow document lists (JNJ-109).
  const isActionableState =
    item.status_flags?.blocked_on_manual_task === true ||
    item.status_flags?.can_cancel_call === true ||
    isAIAuditState(item.state) ||
    item.state === CaseState.REFILL_SCHEDULED ||
    item.state === CaseState.REFILL_HUMAN_AUDIT_NEEDED ||
    item.state === CaseState.REFILL_HUMAN_CALL_NEEDED;

  const hasActions = availableActions.length > 0;
  if (isActionableState && statusColors && hasActions) {
    const handleActionSelect = (action: string) => {
      // Check if action requires call review note input
      if (actionRequiresCallReviewNote(action)) {
        setCallReviewDialogState({
          isOpen: true,
          action,
          caseId: item.id!,
        });
        return;
      }

      if (actionRequiresTaskInput(action)) {
        // Open task dialog - it handles creation internally
        setTaskDialogState({
          isOpen: true,
          action,
          caseId: item.id!,
        });
        return;
      }

      if (actionRequiresIneligibilityNoteInput(action)) {
        setNoteDialogState({
          isOpen: true,
          action,
          caseId: item.id!,
        });
        return;
      }

      if (actionRequiresTextMessageInput(action)) {
        // Open text dialog - it handles creation internally
        setTextDialogState({
          isOpen: true,
          action,
          caseId: item.id!,
        });
        return;
      }

      if (actionRequiresEmailInput(action)) {
        // Open email dialog - it handles creation internally
        setEmailDialogState({
          isOpen: true,
          action,
          caseId: item.id!,
        });
        return;
      }

      // Execute action directly if no note required
      onActionSelect?.(action, item.id!);
    };

    const handleNoteDialogConfirm = (note: string, tag?: string) => {
      // Check if this is an ineligibility note action
      if (actionRequiresIneligibilityNoteInput(noteDialogState.action)) {
        onActionSelect?.(noteDialogState.action, noteDialogState.caseId, {
          ineligibility_note: note,
          ineligibility_tag: tag,
        });
      } else {
        // Execute action with note
        onActionSelect?.(noteDialogState.action, noteDialogState.caseId, {
          note,
        });
      }
      setNoteDialogState({ isOpen: false, action: '', caseId: '' });
    };

    const handleNoteDialogClose = () => {
      setNoteDialogState({ isOpen: false, action: '', caseId: '' });
    };

    const isDisabled = executeAction.isPending || !isActionableState;

    // Categorize actions into sections
    const categorizedActions = categorizeActions(availableActions);

    return (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              data-analytics-id="case:status:change"
              variant="outline"
              size="sm"
              className={`text-xs rounded h-6 px-2 outline-hidden focus-visible:ring-0 ring-0 ${statusColors.bg} hover:${statusColors.bg} ${statusColors.text} hover:${statusColors.text} ${statusColors.border} ${
                isDisabled ? 'cursor-not-allowed opacity-60' : 'hover:opacity-90'
              }`}
              disabled={isDisabled}
              onClick={e => e.stopPropagation()}
            >
              {displayLabel}
              <ChevronsUpDown className={`w-3 h-3 ml-1 ${isDisabled ? 'opacity-50' : ''}`} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-56" onClick={e => e.stopPropagation()}>
            <CaseActionsDropdownItems
              categorizedActions={categorizedActions}
              caseState={item.state}
              onActionSelect={handleActionSelect}
            />
          </DropdownMenuContent>
        </DropdownMenu>

        <ActionInputDialog
          isOpen={noteDialogState.isOpen}
          onClose={handleNoteDialogClose}
          onConfirm={handleNoteDialogConfirm}
          title="Mark as Ineligible"
          inputLabel="Reason for marking as ineligible"
          inputPlaceholder="Enter reason for ineligibility..."
          confirmButtonText="Mark as Ineligible"
          cancelButtonText="Cancel"
          isLoading={executeAction.isPending}
          required
          options={
            actionRequiresIneligibilityTag(noteDialogState.action)
              ? ineligibilityOptions
              : undefined
          }
          defaultTag={
            noteDialogState.action === 'pre_processing_failed_mark_as_ineligible'
              ? 'FAX_INELIGIBLE'
              : undefined
          }
        />

        <ActionTaskDialog
          isOpen={taskDialogState.isOpen}
          onClose={() => setTaskDialogState({ isOpen: false, action: '', caseId: '' })}
          caseRecordId={taskDialogState.caseId}
          action={taskDialogState.action}
          onActionSelect={onActionSelect!}
          title="Create task on Patient Chart"
          dialogDescription="Task details to be updated on Patient Chart"
          confirmButtonText="Send Task"
          cancelButtonText="Cancel"
        />

        <ActionTextDialog
          isOpen={textDialogState.isOpen}
          onClose={() => setTextDialogState({ isOpen: false, action: '', caseId: '' })}
          caseRecordId={textDialogState.caseId}
          action={textDialogState.action}
          onActionSelect={onActionSelect!}
          title="Send Text"
          dialogDescription="Details for text"
          confirmButtonText="Send Text"
          cancelButtonText="Cancel"
        />

        <ActionEmailDialog
          isOpen={emailDialogState.isOpen}
          onClose={() => setEmailDialogState({ isOpen: false, action: '', caseId: '' })}
          caseRecordId={emailDialogState.caseId}
          action={emailDialogState.action}
          onActionSelect={onActionSelect!}
          title="Send Email"
          dialogDescription="Details for email"
          confirmButtonText="Send Email"
          cancelButtonText="Cancel"
        />

        <ActionCallReviewDialog
          isOpen={callReviewDialogState.isOpen}
          onClose={() => setCallReviewDialogState({ isOpen: false, action: '', caseId: '' })}
          caseRecordId={callReviewDialogState.caseId}
          action={callReviewDialogState.action}
          onActionSelect={onActionSelect!}
          workflowType={selectedWorkflow?.workflow_type}
        />
      </>
    );
  }

  // Show regular status badge for other states (same as case details page)
  return (
    item.state &&
    statusColors && (
      <div
        className={`${statusColors.bg} ${statusColors.text} ${statusColors.border} border inline-flex items-center px-3 py-1 rounded text-xs font-medium whitespace-nowrap`}
      >
        {displayLabel}
      </div>
    )
  );
};

// Memoize the component to prevent unnecessary re-renders
export const StatusDropdown = memo(StatusDropdownComponent, (prevProps, nextProps) => {
  return (
    prevProps.item.id === nextProps.item.id &&
    prevProps.item.state === nextProps.item.state &&
    prevProps.selectedWorkflow === nextProps.selectedWorkflow &&
    prevProps.statusConfig === nextProps.statusConfig &&
    prevProps.item.status_flags === nextProps.item.status_flags &&
    prevProps.item.output_data === nextProps.item.output_data &&
    // Assignment gates the "Start Human Audit" action (BI-1246): a reassign
    // changes only these fields (structural sharing keeps status_flags et al.
    // referentially stable), so without them the button never appears/disappears
    // until a full reload.
    prevProps.item.assigned === nextProps.item.assigned &&
    prevProps.item.assigned_user_id === nextProps.item.assigned_user_id
  );
});
